import React, { useMemo } from "react";
import { Select, Tag, Typography } from "antd";
import { BranchesOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { branchLabel, pickableBranches } from "./customerBranches";

const { Text } = Typography;
const SEP = "::";

export const customerName = c =>
  c
    ? [c.company, [c.firstName, c.lastName].filter(Boolean).join(" ")]
        .filter(Boolean)
        .filter((v, i, all) => all.indexOf(v) === i)
        .join(" – ")
    : "";

/**
 * Customer dropdown in which a customer with branches (Niederlassungen) is followed by its
 * branches as indented sub-options. Picking a branch picks its customer too.
 *
 * value: customer id (so it works as a Form.Item child); branch: branch id or null.
 * onChange(customerId, branchId, customer) — branchId is null for the customer itself.
 */
const CustomerSelect = ({ customers = [], value, branch = null, onChange, describe, ...rest }) => {
  const { t: ta } = useTranslation("accounting");

  const options = useMemo(() => {
    const out = [];
    for (const c of Array.isArray(customers) ? customers : []) {
      if (!c?._id) continue;
      const id = String(c._id);
      const name = customerName(c);
      const extra = describe ? describe(c) : "";
      out.push({
        value: id,
        label: name,
        search: `${name} ${extra} ${c.customerNumber || ""}`.toLowerCase(),
        name,
        extra
      });
      const current = String(value) === id ? branch : null;
      for (const b of pickableBranches(c, current)) {
        const bl = branchLabel(b);
        out.push({
          value: `${id}${SEP}${b._id}`,
          label: `${name} · ${bl}`,
          search: `${name} ${bl} ${b.city || ""} ${b.zipCode || ""}`.toLowerCase(),
          isBranch: true,
          branchName: bl,
          place: [b.zipCode, b.city].filter(Boolean).join(" "),
          archived: b.active === false
        });
      }
    }
    return out;
  }, [customers, describe, value, branch]);

  const selected = value ? (branch ? `${value}${SEP}${branch}` : String(value)) : undefined;

  const handle = v => {
    if (!v) return onChange?.(null, null, null);
    const [customerId, branchId] = String(v).split(SEP);
    const customer = customers.find(c => String(c?._id) === customerId) || null;
    onChange?.(customerId, branchId || null, customer);
  };

  return (
    <Select
      showSearch
      allowClear
      {...rest}
      value={selected}
      onChange={handle}
      options={options}
      filterOption={(input, option) => option.search.includes(input.trim().toLowerCase())}
      optionRender={({ data }) =>
        data.isBranch ? (
          <span style={{ paddingInlineStart: 20, display: "inline-flex", gap: 6 }}>
            <BranchesOutlined style={{ opacity: 0.55 }} />
            <span>
              {data.branchName}
              {data.place ? <Text type="secondary"> · {data.place}</Text> : null}
              {data.archived ? <Tag style={{ marginInlineStart: 8 }}>{ta("Archived")}</Tag> : null}
            </span>
          </span>
        ) : (
          <span>
            {data.name}
            {data.extra ? <Text type="secondary"> · {data.extra}</Text> : null}
          </span>
        )
      }
    />
  );
};

export default CustomerSelect;
